import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  SmartToy as BotIcon
} from '@mui/icons-material';
import { ChatMessage, ThreadDetails } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import MessageInput from './MessageInput';
import { getAvatarProps, getUserInitials } from '../../utils/userUtils';

interface ChatWindowProps {
  threadId: string;
  messages: ChatMessage[];
  threadDetails?: ThreadDetails;
  loading?: boolean;
  error?: string;
  onSendMessage: (message: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  threadId,
  messages,
  threadDetails,
  loading = false,
  error,
  onSendMessage,
  onLoadMore,
  hasMore = false
}) => {
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  
  // Debug: Log when props change
  useEffect(() => {
    console.log('🪟 ChatWindow props changed:', {
      threadId,
      messagesCount: messages.length,
      loading,
      firstMessage: messages[0]?.body?.substring(0, 30),
      lastMessage: messages[messages.length - 1]?.body?.substring(0, 30)
    });
  }, [threadId, messages, loading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
    }
  }, [messages, shouldScrollToBottom]);

  useEffect(() => {
    // Reset scroll behavior when thread changes
    setShouldScrollToBottom(true);
  }, [threadId]);

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShouldScrollToBottom(isNearBottom);

      // Load more messages when scrolled to top
      if (scrollTop === 0 && hasMore && onLoadMore) {
        onLoadMore();
      }
    }
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const isMyMessage = (message: ChatMessage) => {
    return message.sender_id === user?.id;
  };

  const getMessageAvatar = (message: ChatMessage) => {
    if (message.kind === 'system') {
      return (
        <Avatar sx={{ width: 32, height: 32, backgroundColor: '#666' }}>
          <BotIcon sx={{ fontSize: 18 }} />
        </Avatar>
      );
    }

    const isMe = isMyMessage(message);
    
    if (isMe && user) {
      // Use current user's profile picture
      return (
        <Avatar 
          {...getAvatarProps(user, 32)}
          sx={{ 
            width: 32, 
            height: 32, 
            backgroundColor: '#533B7D',
            fontSize: '0.875rem',
            ...getAvatarProps(user, 32).sx
          }}
        />
      );
    }

    // For other users, use initials from sender_name or generic icon
    const displayName = message.sender_name || 'User';
    return (
      <Avatar 
        sx={{ 
          width: 32, 
          height: 32, 
          backgroundColor: '#533B7D',
          fontSize: '0.875rem'
        }}
      >
        {getUserInitials(displayName)}
      </Avatar>
    );
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!threadDetails && loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%' 
      }}>
        <CircularProgress sx={{ color: '#533B7D' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Chat Header */}
      {threadDetails && (
        <Paper elevation={1} sx={{ p: 2, borderRadius: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: '#533B7D', width: 40, height: 40 }}>
              {threadDetails.event.title.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {threadDetails.event.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {threadDetails.event.location}
                </Typography>
                <Chip
                  label={threadDetails.thread.scope === 'booking' ? 'Active' : 'Pending'}
                  size="small"
                  sx={{
                    backgroundColor: threadDetails.thread.scope === 'booking' ? '#4caf50' : '#ff9800',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 20
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Messages Container */}
      <Box
        ref={messagesContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          backgroundColor: '#f5f5f5'
        }}
      >
        {loading && messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#533B7D' }} />
          </Box>
        ) : (
          <>
            {/* Load more indicator */}
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Scroll up to load more messages
                </Typography>
              </Box>
            )}

            {/* Messages */}
            {console.log('📝 Rendering messages list, count:', messages.length, 'first:', messages[0]?.body?.substring(0, 20))}
            {messages.map((message, index) => {
              const isMe = isMyMessage(message);
              const isSystem = message.kind === 'system';
              const showAvatar = index === 0 || 
                messages[index - 1].sender_id !== message.sender_id ||
                messages[index - 1].kind !== message.kind;

              if (isSystem) {
                return (
                  <Box key={message.id} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <Chip
                      label={message.body}
                      size="small"
                      sx={{
                        backgroundColor: '#e0e0e0',
                        color: '#666',
                        fontSize: '0.8rem'
                      }}
                    />
                  </Box>
                );
              }

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    flexDirection: isMe ? 'row-reverse' : 'row',
                    mb: 1,
                    alignItems: 'flex-end'
                  }}
                >
                  {/* Avatar */}
                  <Box sx={{ mx: 1 }}>
                    {showAvatar ? getMessageAvatar(message) : <Box sx={{ width: 32 }} />}
                  </Box>

                  {/* Message bubble */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      maxWidth: '70%',
                      backgroundColor: isMe ? '#533B7D' : '#fff',
                      color: isMe ? 'white' : 'black',
                      borderRadius: 2,
                      borderTopLeftRadius: isMe ? 2 : (showAvatar ? 2 : 0.5),
                      borderTopRightRadius: isMe ? (showAvatar ? 2 : 0.5) : 2,
                    }}
                  >
                    {/* Sender name for non-me messages */}
                    {!isMe && showAvatar && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 'bold',
                          color: '#533B7D',
                          display: 'block',
                          mb: 0.5
                        }}
                      >
                        {message.sender_name || 'Unknown'}
                      </Typography>
                    )}

                    {/* Message text */}
                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                      {message.body}
                    </Typography>

                    {/* Timestamp */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        opacity: 0.7,
                        fontSize: '0.7rem'
                      }}
                    >
                      {formatMessageTime(message.created_at)}
                    </Typography>
                  </Paper>
                </Box>
              );
            })}

            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Message Input */}
      {threadDetails && !threadDetails.thread.is_locked && (
        <>
          <Divider />
          <MessageInput onSendMessage={onSendMessage} />
        </>
      )}

      {threadDetails?.thread.is_locked && (
        <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            This chat has been locked
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ChatWindow;
